import React, { useState } from 'react';
import { Theme, makeStyles } from '@material-ui/core';
import { ColDef } from '@ag-grid-community/core';
import { FiSend as SendIcon } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  useArchiveContractTemplateMutation,
  ApiTags,
  ContractField,
  ContractTemplate,
  FieldType,
  InputType,
  ShareContractTemplateInput,
  appAPI,
  useGetContractTemplatesQuery,
  useShareContractTemplateMutation,
} from 'src/services/api';
import StandardPage from 'src/components/UI/StandardPage';
import { LoadingWrapper } from 'src/components/Loading';
import AgGrid, { TextCellRenderer } from 'src/components/AgGrid';
import { BaseActionsMenu } from 'src/components/Dropdowns';
import { FormsIcon, TrashIcon } from 'src/components/Icons';
import { RootState } from 'src/store';
import { FormModal } from 'src/components/Modals';
import { useShareContract } from 'src/hooks/useShareContract';
import { processRecipientIds } from '../FormsV2/ShareFormModal';
import { notify } from 'src/clients/ApiService';
import { UserProfile, getProfileFromUser } from 'src/utils/UserUtils';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(0, 4),
    marginTop: theme.spacing(3),
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      marginTop: 0,
    },
  },
}));

export const templateListcolumns: ColDef[] = [
  {
    headerName: 'Name',
    field: 'name',
    minWidth: 520,
    sortable: true,
  },
  {
    headerName: 'Waiting for submission',
    field: 'requestsCount',
    minWidth: 200,
    sortable: true,
    cellClass: 'rightAlignedColumn',
    headerClass: 'rightAlignedHeader',
    cellRenderer: 'textCellRenderer',
  },
  {
    headerName: 'Submissions',
    field: 'submissionsCount',
    minWidth: 200,
    sortable: true,
    cellClass: 'rightAlignedColumn',
    headerClass: 'rightAlignedHeader',
    cellRenderer: 'textCellRenderer',
  },
  {
    headerName: 'Latest submission',
    field: 'latestSubmissionDate',
    minWidth: 200,
    sortable: true,
    cellClass: 'rightAlignedColumn',
    headerClass: 'rightAlignedHeader',
    cellRenderer: 'textCellRenderer',
  },
];

/**
 * Generates an error message when the share contract request fails.
 * Depending on the number of users, the error message is updated.
 * @param users - Array of user profiles
 * @returns Error message indicating failed contract sharing
 */
function generateErrorMessageForShareContract(users: UserProfile[]) {
  const userNames = users.map(user => `${user.firstName} ${user.lastName}`);

  if (userNames.length === 1) {
    return `Something went wrong sending the contract to ${userNames[0]}.`;
  }

  const limitedUsers = userNames.slice(0, 2).join(' and ');

  if (userNames.length > 2) {
    return `Something went wrong sending the contract to ${limitedUsers} and ${userNames.length -
      2} more.`;
  }

  return `Something went wrong sending the contract to ${limitedUsers}.`;
}

interface TemplateActionMenuProps {
  data: ContractTemplate;
  handleShareModalOpen: (data: ContractTemplate) => void;
}

/**
 * Component to render the table row action for contract templates
 */
const TemplateActionMenu = ({
  data,
  handleShareModalOpen,
}: TemplateActionMenuProps) => {
  const [archiveTemplate] = useArchiveContractTemplateMutation();

  return (
    <BaseActionsMenu
      actions={[
        {
          name: 'Share',
          icon: <SendIcon />,
          onClick: () => handleShareModalOpen(data),
        },
        {
          name: 'Archive',
          icon: <TrashIcon />,
          onClick: () => archiveTemplate({ contractTemplateId: data.id }),
          isDelete: true,
        },
      ]}
    />
  );
};

export const TemplateListTable = () => {
  const dispatch = useDispatch();
  const clients = useSelector((state: RootState) => state.clients.clients);
  const [shareContractTemplate] = useShareContractTemplateMutation();

  const [shareModalInfo, setShareModalInfo] = useState<{
    isOpen: boolean;
    variableInputs: ContractField[];
    selectedTemplateId: string;
    selectedTemplateRef: string;
  }>({
    isOpen: false,
    variableInputs: [],
    selectedTemplateId: '',
    selectedTemplateRef: '',
  });

  const classes = useStyles();
  const {
    data: templates,
    isLoading: areTemplatesLoading,
  } = useGetContractTemplatesQuery();

  /**
   * Opens the contract share modal.
   * If the selected template has variable inputs, the modal will render those inputs.
   * @param rowData - Selected template information
   */
  const handleShareModalOpen = (rowData: ContractTemplate) => {
    if (!rowData) return;

    const variableInputs = rowData.fields?.filter(
      f => f.inputType === InputType.Variable,
    );

    setShareModalInfo({
      isOpen: true,
      variableInputs,
      selectedTemplateId: rowData.id,
      selectedTemplateRef: rowData.ref!,
    });
  };

  const handleShareModalClose = () => {
    setShareModalInfo({
      isOpen: false,
      variableInputs: [],
      selectedTemplateId: '',
      selectedTemplateRef: '',
    });
  };

  /**
   * Shares the template with selected clients.
   * @param values - Form values containing recipient IDs and input field values
   */
  const handleShareContract = async (values: {
    recipientIds: string[];
    fields: Record<string, string>;
  }) => {
    if (!shareModalInfo.selectedTemplateId) return;

    const recipientIds = processRecipientIds(values.recipientIds);

    const queries = recipientIds.map(recipientId => {
      const requestBody: ShareContractTemplateInput = {
        contractTemplateId: shareModalInfo.selectedTemplateId,
        recipientId,
      };

      // Add variable input values in the request body only when there are variable inputs
      if (values.fields && Object.keys(values.fields).length > 0) {
        requestBody.variableValues = Object.entries(values.fields).map(
          item => ({
            id: item[0],
            value: item[1],
          }),
        );
      }
      return shareContractTemplate(requestBody)
        .unwrap()
        .catch(e => ({
          error: e,
          recipientId,
        }));
    });

    const results = await Promise.all(queries);

    // Filter the results to find requests that resulted in errors
    const erroredRequests = results.filter(r => 'error' in r);

    // If there are failed requests, identify the clients associated with these failed requests
    if (erroredRequests.length > 0) {
      const failedRequestClients: UserProfile[] = erroredRequests
        .map(request => {
          const client = clients.find(
            c => 'recipientId' in request && c.id === request.recipientId,
          );
          if (!client) return null;
          return getProfileFromUser(client);
        })
        .filter((c): c is UserProfile => c !== null);

      // Notify with an error status and generate an error message based on failed request clients
      notify({
        status: 'error',
        errorMessage: generateErrorMessageForShareContract(
          failedRequestClients,
        ),
        dispatch,
      });
      return;
    }

    // invalidate "GET: /contracts-template" to fetch the updated data
    dispatch(appAPI.util.invalidateTags([ApiTags.contract_templates]));
    notify({
      status: 'success',
      successMessage: 'Contract template shared successfully.',
      dispatch,
    });
  };

  const searchKey = useSelector((state: RootState) => state.ui.searchValue);

  const shouldShowStandardPage = !templates || templates.length === 0;

  const initialFormValue = {
    recipientId: '',
    fields: shareModalInfo.variableInputs
      ? shareModalInfo.variableInputs.reduce<Record<string, string>>(
          (curr, acc) => ({
            ...curr,
            [acc.id]:
              acc.type === FieldType.Date ? moment().format('MM/DD/YYYY') : '',
          }),
          {},
        )
      : {},
  };

  return (
    <LoadingWrapper isLoading={areTemplatesLoading} hideContentWhileLoading>
      {shouldShowStandardPage ? (
        <StandardPage
          topIcon={<FormsIcon style={{ fontSize: 24 }} />}
          title="No templates have been created yet"
          desc="Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua"
        />
      ) : (
        <div className={classes.root}>
          <AgGrid
            className="agGridRowHover"
            rowData={templates}
            columnDefs={[
              ...templateListcolumns,
              {
                colId: 'actions',
                width: 150,
                headerName: 'Actions',
                suppressSizeToFit: true,
                pinned: 'right',
                cellRenderer: 'templateActionRenderer',
                cellRendererParams: {
                  handleShareModalOpen,
                },
              },
            ]}
            searchKey={searchKey}
            frameworkComponents={{
              templateActionRenderer: TemplateActionMenu,
              textCellRenderer: TextCellRenderer,
            }}
          />
          <FormModal
            initialFormValue={initialFormValue}
            useFormHook={() =>
              useShareContract({
                variableInputs: shareModalInfo.variableInputs,
                selectedTemplateRef: shareModalInfo.selectedTemplateRef,
              })
            }
            open={shareModalInfo.isOpen}
            title="Share contract"
            onClose={handleShareModalClose}
            buttonLabel="Send contract"
            cancelButtonLabel="Dismiss"
            handleSave={handleShareContract}
          />
        </div>
      )}
    </LoadingWrapper>
  );
};
